import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LuListVideo, LuSkipForward, LuPencil } from 'react-icons/lu';

import { Mode, ClientDatabase, IClient } from '~/types/client';
import { Screen } from '~/types/screen';
import { QuizzesAPI } from '~/services/db/quizzes';
import { logEvent } from '~/config/firebase.config';

import styles from './Animator.module.css';
import { AiQuizComposer, AiQuizError, AiQuizReady, AiQuizWriting } from './quizzes/AiQuiz';
import { AiRequest, useQuizGeneration } from './quizzes/useQuizGeneration';

type Level = 'easy' | 'medium' | 'hard' | 'play' | 'round1' | 'round2';
type CatalogSeries = { key: string; label?: string; ids: Partial<Record<Level, string>> };

const tiers = (prefix: string) => ({ easy: `${prefix}-1`, medium: `${prefix}-2`, hard: `${prefix}-3` });

/**
 * Catalogue des quiz Unboared embarqués dans le jeu (unquizz-v2/src/mocks).
 * Le cockpit n'envoie que le quizId : c'est le jeu qui possède le contenu.
 * À maintenir en phase avec `getMockQuizzes` (tri régional du 15/09) : en
 * anglais, quiz natifs EN et pas de quiz franco-centrés ; ailleurs l'inverse.
 * Le jeu retrouve un id même si la TV n'est pas dans la même langue que le
 * téléphone de l'animateur (quizSource.resolveQuiz).
 */
const SHARED: CatalogSeries[] = [
    { key: 'gg', ids: tiers('gg') },
    { key: 'dp', ids: tiers('dp') },
    { key: 'cg', ids: tiers('cg') },
    { key: 'sp', ids: tiers('sp') },
    { key: 'footCdm', ids: tiers('foot-cdm') },
    { key: 'footRec', ids: tiers('foot-rec') },
    { key: 'foot2026', ids: { play: 'foot-2026-1' } },
];
const FRENCH_CENTRIC: CatalogSeries[] = [
    { key: 'cs', ids: tiers('cs') },
    { key: 'ap', ids: tiers('ap') },
    { key: 'footClub', ids: tiers('foot-club') },
];
// Quiz natifs anglais : titres propres, jamais traduits (consoles anglaises seulement).
const ENGLISH_NATIVE: CatalogSeries[] = [
    { key: 'en-gk', label: 'General Knowledge', ids: { round1: 'en-gk-1', round2: 'en-gk-2' } },
    { key: 'en-mu-1', label: 'Music — 80s & 90s', ids: { play: 'en-mu-1' } },
    { key: 'en-mu-2', label: 'Music — 2000s & 2010s', ids: { play: 'en-mu-2' } },
    { key: 'en-mv', label: 'Movies & TV', ids: { play: 'en-mv-1' } },
    { key: 'en-sp-1', label: 'US Sports — NFL, NBA & MLB', ids: { play: 'en-sp-1' } },
    { key: 'en-sp-2', label: 'UK Football', ids: { play: 'en-sp-2' } },
    { key: 'en-ge', label: 'Geography & Travel', ids: { play: 'en-ge-1' } },
    { key: 'en-fd', label: 'Food & Drink', ids: { play: 'en-fd-1' } },
    { key: 'en-sc', label: 'Science & Nature', ids: { play: 'en-sc-1' } },
];

// Quiz de saison (unquizz-v2 `quiz_halloween.json`, toutes langues). Placé en 2e
// position, jamais en 1re : la 1re série est la présélection du cockpit.
const SEASONAL: CatalogSeries[] = [{ key: 'halloween', ids: { play: 'halloween-1' } }];

// Ordre d'affichage hors anglais = ordre historique du catalogue.
const HISTORIC_ORDER = ['cg', 'halloween', 'ap', 'cs', 'dp', 'gg', 'sp', 'footCdm', 'footClub', 'footRec', 'foot2026'];

function catalogFor(lang: string | undefined): CatalogSeries[] {
    if ((lang ?? '').toLowerCase().startsWith('en')) {
        const [first, ...rest] = ENGLISH_NATIVE;
        return [first, ...SEASONAL, ...rest, ...SHARED];
    }
    const byKey = new Map([...SHARED, ...SEASONAL, ...FRENCH_CENTRIC].map((c) => [c.key, c]));
    return HISTORIC_ORDER.map((k) => byKey.get(k)).filter((c): c is CatalogSeries => !!c);
}

type OwnQuiz = { id: string; title: string; questionCount: number };

/** Au-delà, la liste « Mes quiz » gagne un champ de recherche (98 quiz chez certains animateurs). */
const MINE_FILTER_FROM = 8;

/** Sans accents ni casse : « soiree annees 80 » retrouve « Soirée Années 80 ». */
const fold = (text: string) =>
    text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

function filterQuizzes(quizzes: OwnQuiz[], filter: string): OwnQuiz[] {
    const words = fold(filter).split(/\s+/).filter(Boolean);
    if (words.length === 0) return quizzes;
    return quizzes.filter((q) => {
        const title = fold(q.title);
        return words.every((w) => title.includes(w));
    });
}

/**
 * Animator Control — régie du show Unquiz. L'animateur ARME un quiz (les siens,
 * lus dans Firestore, ou le catalogue Unboared embarqué dans le jeu) : le
 * cockpit écrit gameParams.quizId, relayé au jeu qui le résout ; « Lancer la
 * partie » (CTA existante) démarre alors le show avec ce quiz. Pendant la
 * partie, « Question suivante » envoie la commande `show-next`.
 */
export type AiQuizStatus = 'idle' | 'writing' | 'ready' | 'error';

export function UnquizRegiePanel({
    client,
    screen,
    live: providedLive,
    onAiStatus,
}: {
    client: IClient;
    screen?: Screen;
    /** Présence de la TV calculée par le cockpit (qui mémorise les TV ancienne version). */
    live?: boolean;
    /** État de la création IA, pour que le cockpit l'affiche quand la régie est fermée. */
    onAiStatus?: (status: AiQuizStatus) => void;
}) {
    const navigate = useNavigate();
    const live = providedLive ?? screen?.isLive() ?? false;
    const armed = (screen?.gameParams ?? {}) as { quizId?: string; quizTitle?: string };

    const { t, i18n } = useTranslation();
    const catalog = catalogFor(i18n.resolvedLanguage);
    // Le studio (Mes quiz, éditeur) n'existe qu'en français pour l'instant :
    // hors français, on n'y envoie pas l'animateur (audit du 23/09). La régie
    // sur la TV reste disponible pour créer / modifier ses quiz.
    const studioAvailable = (i18n.resolvedLanguage ?? 'fr').startsWith('fr');
    const seriesLabel = (c: CatalogSeries) => c.label ?? t(`animator.unquiz.series.${c.key}`);
    const [ownQuizzes, setOwnQuizzes] = useState<OwnQuiz[] | null>(null);
    const [mineFilter, setMineFilter] = useState('');
    const [series, setSeries] = useState(catalog[0].key);
    // Création IA en une phrase, sur place (décision 23/09 : « créez vos
    // propres quiz » mis en avant dès le cockpit, quiz jouable tout de suite).
    const ai = useQuizGeneration(client.id, i18n.resolvedLanguage);
    const [aiDraft, setAiDraft] = useState<AiRequest | undefined>(undefined);
    const onAiStatusRef = useRef(onAiStatus);
    onAiStatusRef.current = onAiStatus;
    // Un quiz IA prêt ET armé n'a plus rien à signaler : la touche Régie du cockpit
    // reprend le titre du quiz armé.
    const aiStatus: AiQuizStatus =
        ai.state.status === 'ready' && armed.quizId === ai.state.quiz.id ? 'idle' : ai.state.status;
    useEffect(() => {
        onAiStatusRef.current?.(aiStatus);
    }, [aiStatus]);
    const [aiKey, setAiKey] = useState(0);

    useEffect(() => {
        let alive = true; // ignore les réponses périmées (gotcha #86)
        // Tous les quiz de l'établissement (0 = sans limite) : à 25, un animateur
        // qui en a 98 ne retrouvait pas les plus anciens (Tonio Animation, 09/10).
        QuizzesAPI.retrieveQuizzes(client.id, 'unquiz', 0)
            .then((quizzes) => {
                if (!alive) return;
                setOwnQuizzes(
                    quizzes.map((q) => ({
                        id: q.id,
                        title: q.title || q.id,
                        questionCount: q.questions?.length ?? 0,
                    }))
                );
            })
            .catch(() => alive && setOwnQuizzes([]));
        return () => {
            alive = false;
        };
    }, [client.id]);

    const armQuiz = (quizId: string, quizTitle: string) => {
        logEvent('animator_unquiz_arm', client.id, { clientID: client.id, quizId });
        ClientDatabase.setGameParams(client.id, Mode.Anim, {
            ...(screen?.gameParams ?? {}),
            quizId,
            quizTitle,
        }).catch(() => {
            /* l'écran garde son quiz courant si l'écriture échoue */
        });
    };

    // Rythme du quiz (`pace` de set-game-params, unquizz-v2#27) : enchaînement seul, ou
    // la TV attend « Question suivante » à chaque verdict. Sans choix ici, la TV garde le
    // sien (l'écran public, lui, part en automatique).
    const pace = (screen?.gameParams as { pace?: string } | undefined)?.pace;
    const setPace = (next: 'auto' | 'manual') => {
        logEvent('animator_unquiz_pace', client.id, { clientID: client.id, pace: next });
        ClientDatabase.setGameParams(client.id, Mode.Anim, { ...(screen?.gameParams ?? {}), pace: next }).catch(() => {
            /* l'écran garde son rythme si l'écriture échoue */
        });
    };

    const nextBeat = () => {
        logEvent('animator_unquiz_next', client.id, { clientID: client.id });
        ClientDatabase.requestGameCommand(client.id, Mode.Anim, 'show-next').catch(() => {
            /* sans effet si l'écriture échoue ; l'animateur peut réappuyer */
        });
    };

    const catalogEntry = catalog.find((c) => c.key === series) ?? catalog[0];
    const visibleQuizzes = filterQuizzes(ownQuizzes ?? [], mineFilter);

    return (
        <div className={`${styles.btPanel} ${styles.reveal} ${styles.d3}`}>
            <div className={styles.btHead}>
                <span className={styles.btHeadIcon}>
                    <LuListVideo />
                </span>
                {t('animator.unquiz.title')}
            </div>

            {/* Quiz armé : joué au prochain « Lancer la partie » */}
            <div>
                <span className={styles.btLabel}>{t('animator.unquiz.tonight')}</span>
                <div className={styles.btChips}>
                    <span className={`${styles.btChip} ${armed.quizId ? styles.btChipOn : ''}`}>
                        {armed.quizTitle || armed.quizId || t('animator.unquiz.noneArmed')}
                    </span>
                </div>
            </div>

            {/* Créer son propre quiz en une phrase (IA) */}
            {ai.state.status === 'idle' && (
                <AiQuizComposer key={aiKey} compact initial={aiDraft} onSubmit={ai.generate} />
            )}
            {ai.state.status === 'writing' && (
                <AiQuizWriting prompt={ai.state.request.prompt} progress={ai.state.progress} onCancel={ai.reset} />
            )}
            {ai.state.status === 'error' && (
                <AiQuizError
                    code={ai.state.code}
                    detail={ai.state.detail}
                    onRetry={ai.retry}
                    onAddSource={
                        ai.state.request.source
                            ? undefined
                            : () => {
                                  if (ai.state.status === 'error') setAiDraft({ ...ai.state.request, source: '' });
                                  setAiKey((k) => k + 1);
                                  ai.reset();
                              }
                    }
                    onBack={() => {
                        if (ai.state.status === 'error') setAiDraft(ai.state.request);
                        setAiKey((k) => k + 1);
                        ai.reset();
                    }}
                />
            )}
            {ai.state.status === 'ready' && (
                <AiQuizReady
                    quiz={ai.state.quiz}
                    armed={armed.quizId === ai.state.quiz.id}
                    onArm={() => {
                        if (ai.state.status !== 'ready') return;
                        const { quiz } = ai.state;
                        armQuiz(quiz.id, quiz.title);
                        setOwnQuizzes((list) =>
                            list?.some((q) => q.id === quiz.id)
                                ? list
                                : [
                                      { id: quiz.id, title: quiz.title, questionCount: quiz.questions.length },
                                      ...(list ?? []),
                                  ]
                        );
                    }}
                    onReview={
                        studioAvailable
                            ? () =>
                                  ai.state.status === 'ready' &&
                                  navigate(`/animator/${client.id}/quizzes/${ai.state.quiz.id}`)
                            : undefined
                    }
                    onRewrite={() => {
                        if (ai.state.status === 'ready') setAiDraft(ai.state.request);
                        setAiKey((k) => k + 1);
                        ai.reset();
                    }}
                />
            )}

            {/* Mes quiz (Firestore de l'établissement) */}
            <div>
                <span className={styles.btLabel}>
                    {t('animator.unquiz.mine')} {ownQuizzes ? `(${ownQuizzes.length})` : ''}
                </span>
                {!ownQuizzes && <span className={styles.muted}>{t('animator.unquiz.loading')}</span>}
                {ownQuizzes && ownQuizzes.length === 0 && (
                    <span className={styles.muted}>{t('animator.unquiz.noneMine')}</span>
                )}
                {ownQuizzes && ownQuizzes.length > MINE_FILTER_FROM && (
                    <input
                        type="search"
                        className={`${styles.btSelect} ${styles.btSearch}`}
                        value={mineFilter}
                        onChange={(e) => setMineFilter(e.target.value)}
                        disabled={!live}
                        placeholder={t('animator.unquiz.searchMine')}
                        aria-label={t('animator.unquiz.searchMine')}
                    />
                )}
                {ownQuizzes && ownQuizzes.length > 0 && (
                    <select
                        className={styles.btSelect}
                        value={visibleQuizzes.some((q) => q.id === armed.quizId) ? armed.quizId : ''}
                        onChange={(e) => {
                            const q = ownQuizzes.find((x) => x.id === e.target.value);
                            if (q) armQuiz(q.id, q.title);
                        }}
                        disabled={!live}
                    >
                        <option value="" disabled>
                            {mineFilter.trim()
                                ? t('animator.unquiz.matchesMine', { count: visibleQuizzes.length })
                                : t('animator.unquiz.pickMine')}
                        </option>
                        {visibleQuizzes.map((q) => (
                            <option key={q.id} value={q.id}>
                                {q.title} ({t('animator.unquiz.questions', { count: q.questionCount })})
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {/* Catalogue Unboared (embarqué dans le jeu) */}
            <div>
                <span className={styles.btLabel}>{t('animator.unquiz.catalog')}</span>
                <select
                    className={styles.btSelect}
                    value={series}
                    onChange={(e) => setSeries(e.target.value)}
                    disabled={!live}
                >
                    {catalog.map((c) => (
                        <option key={c.key} value={c.key}>
                            {seriesLabel(c)}
                        </option>
                    ))}
                </select>
                <div className={styles.btChips}>
                    {(Object.entries(catalogEntry.ids) as [Level, string][]).map(([level, id]) => {
                        const levelLabel = t(`animator.unquiz.level.${level}`);
                        return (
                            <button
                                key={id}
                                type="button"
                                className={`${styles.btChip} ${armed.quizId === id ? styles.btChipOn : ''}`}
                                onClick={() =>
                                    armQuiz(
                                        id,
                                        level === 'play'
                                            ? seriesLabel(catalogEntry)
                                            : `${seriesLabel(catalogEntry)} — ${levelLabel}`
                                    )
                                }
                                disabled={!live}
                            >
                                {levelLabel}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Studio création : express IA + éditeur complet (mobile & desktop).
                Disponible même écran hors ligne — on peut préparer ses quiz à l'avance. */}
            {studioAvailable && (
                <div>
                    <span className={styles.btLabel}>{t('animator.unquiz.create')}</span>
                    <div className={styles.btChips}>
                        <button
                            type="button"
                            className={styles.btChip}
                            onClick={() => navigate(`/animator/${client.id}/quizzes`)}
                        >
                            <LuPencil style={{ verticalAlign: '-2px', marginRight: 6 }} />
                            {t('animator.unquiz.manage')}
                        </button>
                    </div>
                </div>
            )}

            {/* Pendant la partie : avancer le show sans toucher la TV */}
            <div>
                <span className={styles.btLabel}>{t('animator.unquiz.during')}</span>
                <div className={styles.btChips}>
                    <button type="button" className={styles.btChip} onClick={nextBeat} disabled={!live}>
                        <LuSkipForward style={{ verticalAlign: '-2px', marginRight: 6 }} />
                        {t('animator.unquiz.next')}
                    </button>
                </div>
            </div>

            <div>
                <span className={styles.btLabel}>{t('animator.unquiz.pace')}</span>
                <div className={styles.btChips}>
                    {(['auto', 'manual'] as const).map((p) => (
                        <button
                            key={p}
                            type="button"
                            className={`${styles.btChip} ${pace === p ? styles.btChipOn : ''}`}
                            aria-pressed={pace === p}
                            onClick={() => setPace(p)}
                        >
                            {t(p === 'auto' ? 'animator.unquiz.paceAuto' : 'animator.unquiz.paceManual')}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
